/**
 * Shopify Helper Utilities
 * 
 * This module provides utility functions for data transformation and error handling
 * when working with Shopify Storefront API data.
 */

import {
  ShopifyProduct,
  ShopifyProductVariant,
  ShopifyCart,
  ShopifyCartLine,
  ShopifyMoney,
  ShopifyImage,
  ShopifyConnection,
  ProductCardData,
  CartItemData,
  CartSummary,
  ShopifyApiError,
  ShopifyUserError,
  ProductSortKey,
  SortDirection,
  ProductFilters
} from '../types/shopify';
import { debugError, debugWarn } from './debugLog';

const isShopifyApiErrorInstance = (error: unknown): error is ShopifyApiError => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const candidate = error as { name?: unknown };
  return candidate.name === 'ShopifyApiError';
};

/**
 * Error handling utilities
 */
export class ShopifyHelperError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'ShopifyHelperError';
  }
}

/**
 * Safely extracts nodes from a Shopify connection
 */
export function extractNodes<T>(connection?: ShopifyConnection<T>): T[] {
  if (!connection || !connection.edges) {
    return [];
  }
  
  return connection.edges.map(edge => edge.node).filter(Boolean);
}

/**
 * Safely extracts the first node from a Shopify connection
 */
export function extractFirstNode<T>(connection?: ShopifyConnection<T>): T | null {
  const nodes = extractNodes(connection);
  return nodes.length > 0 ? nodes[0] : null;
}

/**
 * Formats Shopify money amount for display
 */
export function formatMoney(money: ShopifyMoney, options: {
  showCurrency?: boolean;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
} = {}): string {
  const {
    showCurrency = true,
    locale = 'en-GB',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2
  } = options;

  try {
    const amount = parseFloat(money.amount);
    
    if (isNaN(amount)) {
      throw new ShopifyHelperError(`Invalid money amount: ${money.amount}`);
    }

    const formatter = new Intl.NumberFormat(locale, {
      style: showCurrency ? 'currency' : 'decimal',
      currency: showCurrency ? money.currencyCode : undefined,
      minimumFractionDigits,
      maximumFractionDigits
    });

    return formatter.format(amount);
  } catch (error) {
    debugError('Error formatting money:', error);
    return showCurrency ? `${money.currencyCode} ${money.amount}` : money.amount;
  }
}

/**
 * Compares two money amounts
 */
export function compareMoney(a: ShopifyMoney, b: ShopifyMoney): number {
  const amountA = parseFloat(a.amount);
  const amountB = parseFloat(b.amount);
  
  if (isNaN(amountA) || isNaN(amountB)) {
    throw new ShopifyHelperError('Invalid money amounts for comparison');
  }
  
  if (a.currencyCode !== b.currencyCode) {
    debugWarn('Comparing money amounts with different currencies:', a.currencyCode, 'vs', b.currencyCode);
  }
  
  return amountA - amountB;
}

/**
 * Adds two money amounts (must be same currency)
 */
export function addMoney(a: ShopifyMoney, b: ShopifyMoney): ShopifyMoney {
  if (a.currencyCode !== b.currencyCode) {
    throw new ShopifyHelperError(`Cannot add money with different currencies: ${a.currencyCode} and ${b.currencyCode}`);
  }
  
  const amountA = parseFloat(a.amount);
  const amountB = parseFloat(b.amount);
  
  if (isNaN(amountA) || isNaN(amountB)) {
    throw new ShopifyHelperError('Invalid money amounts for addition');
  }
  
  return {
    amount: (amountA + amountB).toFixed(2),
    currencyCode: a.currencyCode
  };
}

/**
 * Multiplies money amount by a quantity
 */
export function multiplyMoney(money: ShopifyMoney, quantity: number): ShopifyMoney {
  const amount = parseFloat(money.amount);
  
  if (isNaN(amount) || isNaN(quantity)) {
    throw new ShopifyHelperError('Invalid values for money multiplication');
  }
  
  return {
    amount: (amount * quantity).toFixed(2),
    currencyCode: money.currencyCode
  };
}

export interface ResolvedVariantPrice {
  amount: number;
  currencyCode: string;
  usedFallback: boolean;
}

export function resolveVariantPrice(
  variant?: ShopifyProductVariant | null,
  product?: ShopifyProduct
): ResolvedVariantPrice {
  const primaryPrice = variant?.price;

  if (primaryPrice?.amount !== undefined && primaryPrice?.amount !== null) {
    const parsed = parseFloat(primaryPrice.amount);
    if (!Number.isNaN(parsed)) {
      return {
        amount: parsed,
        currencyCode: primaryPrice.currencyCode || 'GBP',
        usedFallback: false,
      };
    }
  }

  const fallbackPrice = product?.priceRange?.minVariantPrice;
  if (fallbackPrice?.amount !== undefined && fallbackPrice?.amount !== null) {
    const parsed = parseFloat(fallbackPrice.amount);
    if (!Number.isNaN(parsed)) {
      debugWarn('Variant missing price; using product priceRange fallback', {
        productId: product?.id,
        variantId: variant?.id,
        productTitle: product?.title,
      });
      return {
        amount: parsed,
        currencyCode: fallbackPrice.currencyCode || primaryPrice?.currencyCode || 'GBP',
        usedFallback: true,
      };
    }
  }

  debugWarn('Unable to resolve variant price; defaulting to £0.00', {
    productId: product?.id,
    variantId: variant?.id,
    productTitle: product?.title,
  });

  return {
    amount: 0,
    currencyCode: primaryPrice?.currencyCode || fallbackPrice?.currencyCode || 'GBP',
    usedFallback: true,
  };
}

/**
 * Gets the best available image from a product or variant
 */
export function getBestImage(
  product: ShopifyProduct,
  variant?: ShopifyProductVariant,
  preferredSize?: { width?: number; height?: number }
): ShopifyImage | null {
  // Prefer variant image if available
  if (variant?.image) {
    return variant.image;
  }
  
  // Get product images
  const images = extractNodes(product.images);
  if (images.length === 0) {
    return null;
  }
  
  // If no preferred size, return first image
  if (!preferredSize) {
    return images[0];
  }
  
  // Find image closest to preferred size
  const { width: preferredWidth, height: preferredHeight } = preferredSize;
  
  let bestImage = images[0];
  let bestScore = Infinity;
  
  for (const image of images) {
    if (!image.width || !image.height) continue;
    
    let score = 0;
    if (preferredWidth) {
      score += Math.abs(image.width - preferredWidth);
    }
    if (preferredHeight) {
      score += Math.abs(image.height - preferredHeight);
    }
    
    if (score < bestScore) {
      bestScore = score;
      bestImage = image;
    }
  }
  
  return bestImage;
}

/**
 * Transforms Shopify image URL for different sizes
 */
export function transformImageUrl(
  url: string,
  options: {
    width?: number;
    height?: number;
    crop?: 'center' | 'top' | 'bottom' | 'left' | 'right';
    format?: 'jpg' | 'png' | 'webp';
  } = {}
): string {
  try {
    const { width, height, crop = 'center', format } = options;
    
    // Check if URL has a valid image extension
    const hasValidExtension = /\.(jpg|jpeg|png|webp)$/i.test(url);
    if (!hasValidExtension && (width || height)) {
      // For invalid URLs, just append the size parameters
      const sizeParams: string[] = [];
      if (width && height) {
        sizeParams.push(`${width}x${height}`);
      } else if (width) {
        sizeParams.push(`${width}x`);
      } else if (height) {
        sizeParams.push(`x${height}`);
      }
      return sizeParams.length > 0 ? `${url}_${sizeParams[0]}` : url;
    }
    
    // Remove existing size parameters
    let transformedUrl = url.replace(/(_\d+x\d*|_\d*x\d+|_\d+x|_x\d+)(\.(jpg|jpeg|png|webp))?/i, '');
    
    // Get the file extension
    const extensionMatch = url.match(/\.(jpg|jpeg|png|webp)$/i);
    const originalExtension = extensionMatch ? extensionMatch[0] : '';
    
    // Remove extension temporarily
    transformedUrl = transformedUrl.replace(/\.(jpg|jpeg|png|webp)$/i, '');
    
    // Add new size parameters
    const sizeParams: string[] = [];
    if (width && height) {
      sizeParams.push(`${width}x${height}`);
    } else if (width) {
      sizeParams.push(`${width}x`);
    } else if (height) {
      sizeParams.push(`x${height}`);
    }
    
    if (sizeParams.length > 0) {
      transformedUrl += `_${sizeParams[0]}`;
      
      if (crop !== 'center') {
        transformedUrl += `_crop_${crop}`;
      }
    }
    
    // Add extension back
    const finalExtension = format ? `.${format}` : originalExtension;
    transformedUrl += finalExtension;
    
    return transformedUrl;
  } catch (error) {
    debugError('Error transforming image URL:', error);
    return url; // Return original URL on error
  }
}

/**
 * Determines stock level based on quantity available
 */
export function getStockLevel(
  quantityAvailable?: number,
  lowStockThreshold: number = 5
): 'in-stock' | 'low-stock' | 'out-of-stock' {
  if (quantityAvailable === undefined || quantityAvailable === null) {
    return 'in-stock'; // Assume in stock if quantity not provided
  }
  
  if (quantityAvailable <= 0) {
    return 'out-of-stock';
  }
  
  if (quantityAvailable <= lowStockThreshold) {
    return 'low-stock';
  }
  
  return 'in-stock';
}

/**
 * Gets stock message for display
 */
export function getStockMessage(
  quantityAvailable?: number,
  lowStockThreshold: number = 5
): string | null {
  const stockLevel = getStockLevel(quantityAvailable, lowStockThreshold);
  
  switch (stockLevel) {
    case 'out-of-stock':
      return 'Out of stock';
    case 'low-stock':
      return quantityAvailable === 1 ? 'Only 1 left!' : `Only ${quantityAvailable} left!`;
    default:
      return null;
  }
}

/**
 * Transforms a Shopify product to ProductCardData
 */
export function transformToProductCard(product: ShopifyProduct): ProductCardData {
  try {
    const variants = extractNodes(product.variants);
    const images = extractNodes(product.images);
    const featuredImage = images.length > 0 ? images[0] : undefined;
    
    // Determine overall stock level
    let hasLowStock = false;
    let stockLevel: 'in-stock' | 'low-stock' | 'out-of-stock' = 'in-stock';
    
    if (variants.length > 0) {
      const availableVariants = variants.filter(v => v.availableForSale);
      if (availableVariants.length === 0) {
        stockLevel = 'out-of-stock';
      } else {
        const lowStockVariants = availableVariants.filter(v => 
          getStockLevel(v.quantityAvailable) === 'low-stock'
        );
        if (lowStockVariants.length > 0) {
          hasLowStock = true;
          stockLevel = 'low-stock';
        }
      }
    }
    
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      description: product.description,
      productType: product.productType,
      tags: product.tags,
      availableForSale: product.availableForSale,
      priceRange: product.priceRange,
      compareAtPriceRange: product.compareAtPriceRange,
      featuredImage,
      variants,
      hasLowStock,
      stockLevel
    };
  } catch (error) {
    throw new ShopifyHelperError(`Failed to transform product to card data: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
  }
}

/**
 * Transforms a Shopify cart line to CartItemData
 */
export function transformToCartItem(cartLine: ShopifyCartLine): CartItemData {
  try {
    const variant = cartLine.merchandise;
    const totalPrice = multiplyMoney(variant.price, cartLine.quantity);
    
    return {
      id: cartLine.id,
      variantId: variant.id,
      productId: variant.product?.id || '',
      title: variant.title,
      productTitle: variant.product?.title || '',
      handle: variant.product?.handle || '',
      quantity: cartLine.quantity,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice,
      totalPrice,
      image: variant.image,
      selectedOptions: variant.selectedOptions,
      attributes: cartLine.attributes,
      availableForSale: variant.availableForSale,
      quantityAvailable: variant.quantityAvailable
    };
  } catch (error) {
    throw new ShopifyHelperError(`Failed to transform cart line to item data: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
  }
}

/**
 * Transforms a Shopify cart to CartSummary
 */
export function transformToCartSummary(cart: ShopifyCart): CartSummary {
  try {
    const lines = extractNodes(cart.lines);
    const items = lines.map(transformToCartItem);
    
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    
    return {
      id: cart.id,
      totalQuantity,
      subtotal: cart.cost.subtotalAmount,
      total: cart.cost.totalAmount,
      tax: cart.cost.totalTaxAmount,
      shipping: cart.cost.totalDutyAmount, // Using duty as shipping for now
      checkoutUrl: cart.checkoutUrl,
      isEmpty: totalQuantity === 0,
      items
    };
  } catch (error) {
    throw new ShopifyHelperError(`Failed to transform cart to summary: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
  }
}

/**
 * Filters products based on criteria
 */
export function filterProducts(products: ShopifyProduct[], filters: ProductFilters): ShopifyProduct[] {
  return products.filter(product => {
    // Available filter
    if (filters.available !== undefined && product.availableForSale !== filters.available) {
      return false;
    }
    
    // Product type filter
    if (filters.productType && product.productType !== filters.productType) {
      return false;
    }
    
    // Vendor filter
    if (filters.vendor && product.vendor !== filters.vendor) {
      return false;
    }
    
    // Tag filter
    if (filters.tag && !product.tags.includes(filters.tag)) {
      return false;
    }
    
    // Price range filter
    if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
      const minPrice = parseFloat(product.priceRange.minVariantPrice.amount);
      const maxPrice = parseFloat(product.priceRange.maxVariantPrice.amount);
      
      if (filters.priceMin !== undefined && maxPrice < filters.priceMin) {
        return false;
      }
      
      if (filters.priceMax !== undefined && minPrice > filters.priceMax) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Sorts products based on sort key and direction
 */
export function sortProducts(
  products: ShopifyProduct[],
  sortKey: ProductSortKey,
  direction: SortDirection = 'ASC'
): ShopifyProduct[] {
  const sortedProducts = [...products];
  const multiplier = direction === 'DESC' ? -1 : 1;
  
  sortedProducts.sort((a, b) => {
    let comparison = 0;
    
    switch (sortKey) {
      case 'TITLE':
        comparison = a.title.localeCompare(b.title);
        break;
        
      case 'PRICE':
        const priceA = parseFloat(a.priceRange.minVariantPrice.amount);
        const priceB = parseFloat(b.priceRange.minVariantPrice.amount);
        comparison = priceA - priceB;
        break;
        
      case 'CREATED_AT':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
        
      case 'UPDATED_AT':
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
        
      case 'PRODUCT_TYPE':
        comparison = a.productType.localeCompare(b.productType);
        break;
        
      case 'VENDOR':
        comparison = (a.vendor || '').localeCompare(b.vendor || '');
        break;
        
      case 'ID':
        comparison = a.id.localeCompare(b.id);
        break;
        
      case 'BEST_SELLING':
      case 'RELEVANCE':
      default:
        // For these, we maintain original order
        comparison = 0;
        break;
    }
    
    return comparison * multiplier;
  });
  
  return sortedProducts;
}

/**
 * Extracts unique values from products for filtering
 */
export function extractFilterOptions(products: ShopifyProduct[]): {
  productTypes: string[];
  vendors: string[];
  tags: string[];
  priceRange: { min: number; max: number };
} {
  const productTypes = new Set<string>();
  const vendors = new Set<string>();
  const tags = new Set<string>();
  let minPrice = Infinity;
  let maxPrice = -Infinity;
  
  products.forEach(product => {
    // Product types
    if (product.productType) {
      productTypes.add(product.productType);
    }
    
    // Vendors
    if (product.vendor) {
      vendors.add(product.vendor);
    }
    
    // Tags
    product.tags.forEach(tag => tags.add(tag));
    
    // Price range
    const productMinPrice = parseFloat(product.priceRange.minVariantPrice.amount);
    const productMaxPrice = parseFloat(product.priceRange.maxVariantPrice.amount);
    
    if (productMinPrice < minPrice) minPrice = productMinPrice;
    if (productMaxPrice > maxPrice) maxPrice = productMaxPrice;
  });
  
  return {
    productTypes: Array.from(productTypes).sort(),
    vendors: Array.from(vendors).sort(),
    tags: Array.from(tags).sort(),
    priceRange: {
      min: minPrice === Infinity ? 0 : minPrice,
      max: maxPrice === -Infinity ? 0 : maxPrice
    }
  };
}

/**
 * Validates Shopify ID format
 */
export function isValidShopifyId(id: string): boolean {
  return /^gid:\/\/shopify\/\w+\/\d+$/.test(id);
}

/**
 * Extracts numeric ID from Shopify GID
 */
export function extractNumericId(gid: string): string | null {
  const match = gid.match(/^gid:\/\/shopify\/\w+\/(\d+)$/);
  return match ? match[1] : null;
}

/**
 * Handles Shopify API errors
 */
export function handleShopifyError(error: unknown): ShopifyApiError {
  // Check if it's already a ShopifyApiError by checking the name property
  if (isShopifyApiErrorInstance(error)) {
    return error as ShopifyApiError;
  }
  
  if (error instanceof Error) {
    const shopifyError = new Error(error.message) as ShopifyApiError;
    shopifyError.name = 'ShopifyApiError';
    return shopifyError;
  }
  
  const unknownError = new Error('Unknown Shopify API error') as ShopifyApiError;
  unknownError.name = 'ShopifyApiError';
  return unknownError;
}

/**
 * Formats Shopify user errors for display
 */
export function formatUserErrors(errors: ShopifyUserError[]): string[] {
  return errors.map(error => {
    if (error.field && error.field.length > 0) {
      const fieldName = error.field[error.field.length - 1];
      return `${fieldName}: ${error.message}`;
    }
    return error.message;
  });
}

/**
 * Checks if a product has any available variants
 */
export function hasAvailableVariants(product: ShopifyProduct): boolean {
  const variants = extractNodes(product.variants);
  return variants.some(variant => variant.availableForSale);
}

/**
 * Gets the default variant for a product
 */
export function getDefaultVariant(product: ShopifyProduct): ShopifyProductVariant | null {
  const variants = extractNodes(product.variants);
  
  if (variants.length === 0) {
    return null;
  }
  
  // Prefer available variants
  const availableVariants = variants.filter(v => v.availableForSale);
  if (availableVariants.length > 0) {
    return availableVariants[0];
  }
  
  // Return first variant if none are available
  return variants[0];
}

/**
 * Calculates discount percentage
 */
export function calculateDiscountPercentage(originalPrice: ShopifyMoney, salePrice: ShopifyMoney): number {
  const original = parseFloat(originalPrice.amount);
  const sale = parseFloat(salePrice.amount);
  
  if (original <= 0 || sale >= original) {
    return 0;
  }
  
  return Math.round(((original - sale) / original) * 100);
}

/**
 * Debounce utility for search and filtering
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Format price with currency symbol
 * 
 * @param amount - Price amount as number or string
 * @param currencyCode - Currency code (e.g., 'GBP', 'USD')
 * @returns Formatted price string
 */
export function formatPrice(amount: number | string, currencyCode: string = 'GBP'): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) {
    return '£0.00';
  }

  // Currency symbol mapping
  const currencySymbols: Record<string, string> = {
    'GBP': '£',
    'USD': '$',
    'EUR': '€',
    'CAD': 'C$',
    'AUD': 'A$',
  };

  const symbol = currencySymbols[currencyCode] || currencyCode;
  
  return `${symbol}${numericAmount.toFixed(2)}`;
}

/**
 * Validate that a price is in UK currency (GBP)
 */
export function validateUKCurrency(money: ShopifyMoney): boolean {
  return money.currencyCode === 'GBP';
}

/**
 * Ensure price formatting is consistent with UK currency
 */
export function formatUKPrice(amount: number | string): string {
  return formatPrice(amount, 'GBP');
}

/**
 * Convert currency display to UK format if needed
 */
export function ensureUKCurrencyDisplay(money: ShopifyMoney): string {
  if (money.currencyCode !== 'GBP') {
    debugWarn(`Price in ${money.currencyCode} instead of expected GBP: ${money.amount}`);
  }
  return formatPrice(money.amount, money.currencyCode);
}

/**
 * Validate cart currency and log warnings
 */
export function validateCartCurrency(cart: ShopifyCart): boolean {
  const isValidCurrency = cart.cost.totalAmount.currencyCode === 'GBP';
  
  if (!isValidCurrency) {
    debugWarn(`Cart currency mismatch: expected GBP, got ${cart.cost.totalAmount.currencyCode}`);
    debugWarn('Cart total:', cart.cost.totalAmount.amount, cart.cost.totalAmount.currencyCode);
  }
  
  return isValidCurrency;
}
